<template>
  <div
    class="bg-white border rounded-xl shadow-sm p-5 hover:shadow-lg transition-shadow duration-200 flex flex-col cursor-pointer"
    :class="isSelected ? 'border-teal-400 ring-2 ring-teal-100 shadow-md' : 'border-gray-200'"
    @click="$emit('select', contractor)"
  >
    <!-- Header -->
    <div class="flex items-start justify-between mb-4">
      <div class="flex items-center gap-4">
        <div class="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center border overflow-hidden">
          <img
            v-if="photoUrl"
            :src="photoUrl"
            alt="Contractor photo"
            class="h-full w-full object-cover"
          />
          <div v-else class="flex h-full w-full items-center justify-center">
            <User class="h-6 w-6 text-gray-500" />
          </div>
        </div>
        <div>
          <h3 class="font-semibold text-lg text-gray-900">{{ contractor.full_name }}</h3>
          <p v-if="contractor.current_role" class="text-sm text-gray-500">{{ contractor.current_role }}</p>
          <p v-if="hasCurrentCompany" class="text-sm text-gray-500 flex items-center gap-1">
            <Building2 class="h-4 w-4 text-gray-400" /> {{ currentCompanyDisplay }}
          </p>
        </div>
      </div>
      <div class="text-right">
        <span class="text-[11px] tracking-[0.08em] text-gray-500 font-semibold block">Joined on</span>
        <span class="text-xs font-semibold text-gray-800">{{ formattedApplicationDate }}</span>
      </div>
    </div>

    <!-- Details Grid -->
    <div class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-5">
      <div class="flex items-center gap-2 text-gray-500">
        <MapPin class="h-4 w-4 text-gray-400" />
        <span>{{ contractor.location }}</span>
      </div>
      <div class="flex items-center gap-2 text-gray-500">
        <Clock class="h-4 w-4 text-gray-400" />
        <span class="font-medium text-gray-600">
          {{ contractor.availability }}
        </span>
      </div>
      <div class="flex items-center gap-2 text-gray-600">
        <DollarSign class="h-4 w-4 text-gray-400" />
        <span class="font-medium">{{ contractor.rate }} / {{ contractor.rate_type }}</span>
      </div>
      <div class="flex items-center gap-2 text-gray-500">
        <Award class="h-4 w-4 text-gray-400" />
        <span>{{ contractor.totalExperience }}</span>
      </div>
    </div>

    <!-- Previous Companies -->
    <div class="flex items-center gap-2 text-xs text-gray-500 mb-4">
      <span class="font-semibold text-gray-600">Previous companies:</span>
      <span>{{ companiesDisplay || 'N/A' }}</span>
    </div>

    <!-- Skills -->
    <div class="mb-5" v-if="contractor.skills && contractor.skills.length > 0">
      <div class="flex flex-wrap gap-2">
        <span
          v-for="skill in displaySkills"
          :key="skill"
          class="px-2 py-1 text-xs font-medium rounded-full bg-teal-50 text-teal-700 border border-teal-200"
        >
          {{ skill }}
        </span>
        <span
          v-if="remainingSkills > 0"
          class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200"
        >
          +{{ remainingSkills }} more
        </span>
      </div>
    </div>
    
    <div class="border-t border-gray-200 pt-4 mt-auto flex flex-col sm:flex-row gap-2">
        <Button
          class="flex-1"
          variant="outline"
          size="sm"
          @click="$emit('view-profile', contractor)"
        >
          <User class="h-4 w-4 mr-2" />
          <span>View Profile</span>
        </Button>

        <Button
          class="flex-1 hidden"
          variant="outline"
          size="sm"
          @click="$emit('message', contractor.contractorId)"
        >
          <MessageCircle class="h-4 w-4 mr-2" />
          <span>Message</span>
        </Button>

        <Button
          type="button"
          class="flex-1"
          variant="outline"
          size="sm"
          @click.prevent="$emit('download-resume', contractor.contractorId)"
        >
          <Download class="h-4 w-4 mr-2" />
          <span>Resume</span>
        </Button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Button } from '@/components/marketplace/ui';
import { User, Download, MapPin, Clock, Award, MessageCircle, DollarSign, Calendar, Building2 } from 'lucide-vue-next';
import { formatDate } from '@/utils/formatters';
import genericService from '@/services/marketplace/generic.service';

const props = defineProps({
  contractor: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['view-profile', 'download-resume', 'message', 'select']);

const displaySkills = computed(() => {
  return props.contractor.skills ? props.contractor.skills.slice(0, 4) : [];
});

const remainingSkills = computed(() => {
  if (!props.contractor.skills) return 0;
  const remaining = props.contractor.skills.length - 4;
  return remaining > 0 ? remaining : 0;
});

const hasCurrentCompany = computed(() => {
  const company = props.contractor.current_company;
  if (!company) return false;
  const trimmed = String(company).trim();
  return trimmed.length > 0 && trimmed.toLowerCase() !== 'n/a';
});
const currentCompanyDisplay = computed(() => (hasCurrentCompany.value ? props.contractor.current_company : ''));

const companiesDisplay = computed(() => {
  let companies = props.contractor.companies;
  if (typeof companies === 'string') {
    try {
      companies = JSON.parse(companies);
    } catch (e) {
      companies = companies.split(',').map(c => c.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(companies) || !companies.length) return '';
  const filtered = companies
    .filter(Boolean)
    .map(c => String(c).trim())
    .filter(c => c && c.toLowerCase() !== 'n/a')
    .filter(c => c.toLowerCase() !== 'self employed' && c.toLowerCase() !== 'self-employed')
    .filter(c => !hasCurrentCompany.value || c !== props.contractor.current_company);
  if (!filtered.length) return '';
  const slice = filtered.slice(0, 3);
  const remaining = companies.length - slice.length;
  if (!slice.length) return '';
  return remaining > 0 ? `${slice.join(', ')} and ${remaining} more` : slice.join(', ');
});

const photoUrl = computed(() => {
  return (
    props.contractor.photo ||
    genericService.getProfilePictureLink(
      props.contractor.photo_hash,
      props.contractor.profile_picture_s3_key
    )
  );
});


const formattedApplicationDate = computed(() => {
  return props.contractor ? formatDate(props.contractor.application_date) : '';
});

const getAvailabilityColor = (availability) => {
  switch (availability) {
    case 'Available':
      return 'text-green-700';
    case 'Busy':
      return 'text-amber-700';
    case 'On Project':
      return 'text-sky-700';
    default:
      return 'text-gray-700';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Accepted':
      return 'bg-green-100 text-green-800';
    case 'Pending':
      return 'bg-amber-100 text-amber-800';
    case 'Declined':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
</script>
